import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getOrderUuid, getProfile, getSelectedExhibitionId } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import {
  BookedProducts,
  BookingTariff,
  isBookingProductTypePackage,
  isBookingProductTypeTariff,
  PromoCodeDiscounts,
  RedeemPromoCodeBookingTariff
} from '@products/models/booking.model';
import {
  PromoCode,
  PromoCodeResponse,
  PromoCodeTicketResponse,
  RedeemedPromoCode,
  RedeemPromoCodeRequest,
  RedeemPromoCodeRequestTicket,
  RedeemPromoCodeResponse,
  ReleasePromoCodeRequest
} from '@products/models/smart-promotions';
import { VoucherWarningMessageService } from '@products/services/voucher-warning-message.service';
import { AppConstants } from '@shared/app-constants';
import { environment } from '@src/environments/environment';
import { getBookedProducts } from '@store/products/booking/product/product-booking.selectors';
import { BookingSmartPromotionActions } from '@store/products/booking/smart-promotion/booking-smart-promotion.actions';
import {
  selectAllDiscountPromoCodes,
  selectIsPromoCodeDiscountApplied
} from '@store/products/booking/smart-promotion/booking-smart-promotion.selectors';
import {
  filter as _filter,
  map as _map,
  cloneDeep,
  findIndex,
  forEach,
  includes,
  isEmpty,
  isNil,
  isNull,
  join
} from 'lodash';
import { DateTime } from 'luxon';
import { catchError, combineLatest, filter, first, map, Observable, of, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BookingSmartPromotionService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);
  private readonly voucherWarningMessageService = inject(VoucherWarningMessageService);

  setBookingPromoCodeDiscount$(promoCode?: PromoCode) {
    return this.preparePromoCodeRequest$(promoCode).pipe(
      switchMap(({ redeemPromoCodeRequest, redeemPromoCodeBookedTariffs }) =>
        this.postRedeemPromoCodes$(redeemPromoCodeRequest).pipe(
          map(({ ticketResults, promoCodeResults }) =>
            this.mapPromoCodeResponseTicketsToPartialBookingTariffsAndSetPromoCodeResultsValidity(
              ticketResults,
              promoCodeResults,
              redeemPromoCodeBookedTariffs
            )
          ),
          tap(({ notAppliedRedeemedPromoCodes }) =>
            this.setInvalidPromoCodesWarningMessage(notAppliedRedeemedPromoCodes)
          ),
          map(({ discountedPartialBookingTariffs, appliedRedeemedPromoCodes }) =>
            BookingSmartPromotionActions.setBookedProductsPromoCodeDiscount({
              discountedPartialBookingTariffs,
              appliedRedeemedPromoCodes
            })
          )
        )
      ),
      catchError(() =>
        of(
          BookingSmartPromotionActions.setBookedProductsPromoCodeDiscount({
            discountedPartialBookingTariffs: [],
            appliedRedeemedPromoCodes: null
          })
        )
      )
    );
  }

  releasePromoCodeDiscountReservation$() {
    return this.preparePromoCodeReleaseRequest$().pipe(
      switchMap((releasePromoCodeRequest) =>
        this.postReleasePromoCodes$(releasePromoCodeRequest).pipe(map(() => releasePromoCodeRequest.uuid))
      )
    );
  }

  mapRedeemedPromoCodesToPromoCodes(redeemedPromoCodes: RedeemedPromoCode[]): PromoCode[] {
    return _map(redeemedPromoCodes, ({ promoCode }) => promoCode);
  }

  private postRedeemPromoCodes$(redeemPromoCodeRequest: RedeemPromoCodeRequest): Observable<RedeemPromoCodeResponse> {
    return this.http.post<RedeemPromoCodeResponse>(
      `${environment.protocol}${environment.webApiUrl}/promocode/redeem`,
      redeemPromoCodeRequest
    );
  }

  private postReleasePromoCodes$(releasePromoCodeRequest: ReleasePromoCodeRequest) {
    return this.http.post<RedeemPromoCodeResponse>(
      `${environment.protocol}${environment.webApiUrl}/promocode/release`,
      releasePromoCodeRequest
    );
  }

  private preparePromoCodeRequest$(removeOrRedeemPromoCode?: PromoCode): Observable<{
    redeemPromoCodeBookedTariffs: RedeemPromoCodeBookingTariff[];
    redeemPromoCodeRequest: RedeemPromoCodeRequest;
  }> {
    return combineLatest({
      orderUuid: this.store.select(getOrderUuid),
      profile: this.store.select(getProfile),
      selectedExhibitionId: this.store.select(getSelectedExhibitionId),
      bookedProducts: this.store.select(getBookedProducts),
      redeemedDiscountPromoCodes: this.store.select(selectAllDiscountPromoCodes)
    }).pipe(
      first(
        ({ orderUuid, selectedExhibitionId, bookedProducts, redeemedDiscountPromoCodes }) =>
          (!isNil(removeOrRedeemPromoCode) || !isEmpty(redeemedDiscountPromoCodes)) &&
          !isNil(orderUuid) &&
          !isNil(selectedExhibitionId) &&
          !isEmpty(bookedProducts)
      ),
      map(({ orderUuid, profile, selectedExhibitionId, bookedProducts, redeemedDiscountPromoCodes }) => {
        const { redeemPromoCodeBookedTariffs, redeemPromoCodeRequestTickets } =
          this.mapBookedProductTypesToRedeemPromoCodeRequestTickets(bookedProducts);

        const promoCodes = this.mapRedeemedPromoCodesToPromoCodes(redeemedDiscountPromoCodes);
        const promoCodeIndex = promoCodes.findIndex((promoCode) => promoCode === removeOrRedeemPromoCode);
        const isPromoCodeRemove = promoCodeIndex !== AppConstants.INDEX_NOT_FOUND;
        const isPromoCodeRedeem = !isPromoCodeRemove && !isEmpty(removeOrRedeemPromoCode);

        if (isPromoCodeRemove) {
          promoCodes.splice(promoCodeIndex, 1);
        }

        if (isPromoCodeRedeem) {
          promoCodes.push(removeOrRedeemPromoCode);
        }

        return {
          redeemPromoCodeBookedTariffs,
          redeemPromoCodeRequest: {
            uuid: orderUuid,
            eventId: selectedExhibitionId,
            userId: profile?.id,
            tickets: redeemPromoCodeRequestTickets,
            promoCodes
          }
        };
      })
    );
  }

  private preparePromoCodeReleaseRequest$(): Observable<ReleasePromoCodeRequest> {
    return combineLatest({
      orderUuid: this.store.select(getOrderUuid),
      profile: this.store.select(getProfile),
      selectedExhibitionId: this.store.select(getSelectedExhibitionId),
      isPromoCodeDiscountApplied: this.store.select(selectIsPromoCodeDiscountApplied)
    }).pipe(
      first(),
      filter(
        ({ orderUuid, selectedExhibitionId, isPromoCodeDiscountApplied }) =>
          !isNil(orderUuid) && !isNil(selectedExhibitionId) && isPromoCodeDiscountApplied
      ),
      map(({ orderUuid, profile, selectedExhibitionId }) => ({
        uuid: orderUuid,
        eventId: selectedExhibitionId,
        userId: profile?.id
      }))
    );
  }

  private mapBookedProductTypesToRedeemPromoCodeRequestTickets(bookedProducts: BookedProducts) {
    let bookedTicketIndex = 0;
    let redeemPromoCodeRequestTickets: RedeemPromoCodeRequestTicket[] = [];

    const redeemPromoCodeBookedTariffs: RedeemPromoCodeBookingTariff[] = [];

    const setRedeemPromoCodeRequestTicket = (bookingTariff: BookingTariff) => {
      const { ticketPersonId, packageIndex, packageNumber, count, contingentReservations } = bookingTariff;

      const redeemPromoCodeBookedTariff: RedeemPromoCodeBookingTariff = {
        ...cloneDeep(bookingTariff),
        redeemPromoCodeTicketIndexes: []
      };

      const bookedCountRedeemPromoCodeRequestTickets = Array.from(
        Array(count),
        (_, index): RedeemPromoCodeRequestTicket => {
          const redeemPromoCodeRequestTicket = {
            ticketPersonId,
            packageNumber,
            packageIndex,
            ticketIndex: bookedTicketIndex,
            day: contingentReservations[index]
              ? DateTime.fromJSDate(contingentReservations[index].fromDate).toISODate()
              : null
          };

          redeemPromoCodeBookedTariff.redeemPromoCodeTicketIndexes.push(bookedTicketIndex);

          bookedTicketIndex++;

          return redeemPromoCodeRequestTicket;
        }
      );

      redeemPromoCodeRequestTickets = [...redeemPromoCodeRequestTickets, ...bookedCountRedeemPromoCodeRequestTickets];

      redeemPromoCodeBookedTariffs.push(redeemPromoCodeBookedTariff);
    };

    forEach(bookedProducts, (bookedProductType) => {
      if (isBookingProductTypeTariff(bookedProductType)) {
        forEach(bookedProductType.tariffs, setRedeemPromoCodeRequestTicket);
      } else if (isBookingProductTypePackage(bookedProductType)) {
        forEach(bookedProductType.productTypes, (bookedPackageProductType) => {
          if (isBookingProductTypeTariff(bookedPackageProductType)) {
            forEach(bookedPackageProductType.tariffs, setRedeemPromoCodeRequestTicket);
          }
        });
      }
    });

    return { redeemPromoCodeBookedTariffs, redeemPromoCodeRequestTickets };
  }

  private mapPromoCodeResponseTicketsToPartialBookingTariffsAndSetPromoCodeResultsValidity(
    ticketResults: PromoCodeTicketResponse[],
    promoCodeResults: PromoCodeResponse[],
    redeemPromoCodeBookedTariffs: RedeemPromoCodeBookingTariff[]
  ) {
    const initialRedeemedPromoCodes = this.mapPromoCodeResponsePromoCodesToRedeemedPromoCodes(promoCodeResults);
    const revalidateOrderLevelRedeemedPromoCodes =
      this.revalidateOrderLevelRedeemedPromoCodes(initialRedeemedPromoCodes);
    const appliedOrderLevelRedeemedPromoCodes = _filter(
      revalidateOrderLevelRedeemedPromoCodes,
      ({ isRedeemedAndApplied }) => isRedeemedAndApplied
    );

    const partialBookedTariffs: Partial<BookingTariff>[] = [];

    forEach(
      ticketResults,
      ({
        ticketPersonId,
        packageNumber,
        packageIndex,
        ticketIndex,
        fullPrice,
        discountedPrice,
        discountPercentage,
        promoCodeResults
      }) => {
        const bookingVoucherTariff = redeemPromoCodeBookedTariffs.find(
          (redeemedPromoCodeBookingTariff) =>
            !isEmpty(redeemedPromoCodeBookingTariff.voucherCode) &&
            redeemedPromoCodeBookingTariff.ticketPersonId === ticketPersonId &&
            redeemedPromoCodeBookingTariff.packageNumber === packageNumber &&
            redeemedPromoCodeBookingTariff.packageIndex === packageIndex &&
            includes(redeemedPromoCodeBookingTariff.redeemPromoCodeTicketIndexes, ticketIndex)
        );

        let partialBookedTariffIndex = AppConstants.INDEX_NOT_FOUND;

        if (bookingVoucherTariff) {
          partialBookedTariffIndex = findIndex(
            partialBookedTariffs,
            (partialBookedTariff) =>
              partialBookedTariff.ticketPersonId === bookingVoucherTariff.ticketPersonId &&
              partialBookedTariff.voucherCode === bookingVoucherTariff.voucherCode &&
              partialBookedTariff.packageNumber === bookingVoucherTariff.packageNumber &&
              partialBookedTariff.packageIndex === bookingVoucherTariff.packageIndex
          );
        } else {
          partialBookedTariffIndex = findIndex(
            partialBookedTariffs,
            (partialBookedTariff) =>
              partialBookedTariff.ticketPersonId === ticketPersonId &&
              partialBookedTariff.packageNumber === packageNumber &&
              partialBookedTariff.packageIndex === packageIndex
          );
        }

        const appliedTariffLevelRedeemedPromoCodes =
          this.mapPromoCodeResponsePromoCodesToAppliedPromoCodes(promoCodeResults);

        const appliedRedeemedPromoCodes = [
          ...appliedOrderLevelRedeemedPromoCodes,
          ...appliedTariffLevelRedeemedPromoCodes
        ];

        if (partialBookedTariffIndex === AppConstants.INDEX_NOT_FOUND) {
          const promoCodeDiscounts: PromoCodeDiscounts = {};

          forEach(appliedRedeemedPromoCodes, ({ promoCode, discountPercentage }) => {
            this.revalidateOrderLevelRedeemedPromoCodeByTariffLevelRedeemedPromoCode(
              promoCode,
              revalidateOrderLevelRedeemedPromoCodes,
              appliedTariffLevelRedeemedPromoCodes
            );

            promoCodeDiscounts[promoCode] = {
              discountCount: 1,
              discountPercentage
            };
          });

          const partialBookedTariff: Partial<BookingTariff> = {
            ticketPersonId,
            packageNumber,
            packageIndex,
            price: fullPrice,
            discountedPrice,
            discountPercentage,
            promoCodeDiscounts
          };

          if (bookingVoucherTariff) {
            partialBookedTariff.voucherCode = bookingVoucherTariff.voucherCode;
          }

          partialBookedTariffs.push(partialBookedTariff);
        } else {
          const { promoCodeDiscounts } = partialBookedTariffs[partialBookedTariffIndex];

          forEach(appliedRedeemedPromoCodes, ({ promoCode, discountPercentage }) => {
            if (promoCodeDiscounts[promoCode]) {
              promoCodeDiscounts[promoCode].discountCount++;
            } else {
              promoCodeDiscounts[promoCode] = {
                discountCount: 1,
                discountPercentage
              };
            }
          });
        }
      }
    );

    const appliedRedeemedPromoCodes = _filter(
      revalidateOrderLevelRedeemedPromoCodes,
      ({ isRedeemedAndApplied }) => isRedeemedAndApplied
    );

    const notAppliedRedeemedPromoCodes = _filter(
      revalidateOrderLevelRedeemedPromoCodes,
      ({ isRedeemedAndApplied }) => !isRedeemedAndApplied
    );

    return {
      discountedPartialBookingTariffs: partialBookedTariffs,
      appliedRedeemedPromoCodes,
      notAppliedRedeemedPromoCodes
    };
  }

  private mapPromoCodeResponsePromoCodesToRedeemedPromoCodes(responsePromoCodes: PromoCodeResponse[]) {
    return (
      _map(responsePromoCodes, ({ promoCode, discountPercentage, isValid }) => ({
        promoCode,
        discountPercentage,
        isRedeemedAndApplied: isValid
      })) || []
    );
  }

  private mapPromoCodeResponsePromoCodesToAppliedPromoCodes(
    responsePromoCodes: PromoCodeResponse[]
  ): RedeemedPromoCode[] {
    return (
      _map(
        _filter(responsePromoCodes, ({ isValid }) => isValid),
        ({ promoCode, discountPercentage, isValid }) => ({
          promoCode,
          discountPercentage,
          isRedeemedAndApplied: isValid
        })
      ) || []
    );
  }

  private revalidateOrderLevelRedeemedPromoCodes(redeemedPromoCodes: RedeemedPromoCode[]): RedeemedPromoCode[] {
    return _map(redeemedPromoCodes, (redeemedPromoCode) => {
      const { discountPercentage, isRedeemedAndApplied: isValid } = redeemedPromoCode;

      if (!isValid) {
        return redeemedPromoCode;
      }

      const isValidOrderLevelPromoCodeWithDiscount = !isNull(discountPercentage) && isValid;

      return {
        ...redeemedPromoCode,
        isRedeemedAndApplied: isValidOrderLevelPromoCodeWithDiscount
      };
    });
  }

  private revalidateOrderLevelRedeemedPromoCodeByTariffLevelRedeemedPromoCode(
    tariffPromoCode: PromoCode,
    revalidateOrderLevelRedeemedPromoCodes: RedeemedPromoCode[],
    appliedTariffLevelRedeemedPromoCodes: RedeemedPromoCode[]
  ) {
    const isAppliedOrderLevelRedeemedPromoCode = isNil(appliedTariffLevelRedeemedPromoCodes[tariffPromoCode]);

    if (isAppliedOrderLevelRedeemedPromoCode) {
      const orderLevelCurrentTariffPromoCodeIndex = findIndex(
        revalidateOrderLevelRedeemedPromoCodes,
        ({ promoCode: orderLevelRedeemedPromoCode }) => orderLevelRedeemedPromoCode === tariffPromoCode
      );

      if (orderLevelCurrentTariffPromoCodeIndex !== AppConstants.INDEX_NOT_FOUND) {
        revalidateOrderLevelRedeemedPromoCodes[orderLevelCurrentTariffPromoCodeIndex].isRedeemedAndApplied = true;
      }
    }
  }

  private setInvalidPromoCodesWarningMessage(invalidRedeemedPromoCodes: RedeemedPromoCode[]) {
    if (isEmpty(invalidRedeemedPromoCodes)) {
      return;
    }

    const invalidPromoCodes = join(
      _map(invalidRedeemedPromoCodes, ({ promoCode }) => promoCode),
      ', '
    );

    this.voucherWarningMessageService.setInvalidPromoCodeWarningMessage(invalidPromoCodes);
  }
}
